import React from "react";
import AdsenseShopping from "web/react/components/adsense-shopping/adsense-shopping";
import { BrandAdContainer } from "web/react/components/brand-ad-container/brand-ad-container";
import { CuratedAds } from "web/react/components/curated-ads/curated-ads";
import { MoreLikeThisGrid } from "web/react/components/more-like-this-grid/more-like-this-grid";
import { RecentlyViewed } from "web/react/components/recently-viewed/recently-viewed";
import { RelatedSearchesRail } from "web/react/components/related-searches-rail/related-searches-rail";
import { VStack } from "web/react/emo/v-stack";
import { View } from "web/react/emo/view";
import {
    ProductPurchaseType,
    useInStockProductPageContext,
} from "web/react/pages/product/in-stock/in-stock-product-page/in-stock-product-page.context";
import { gettext } from "web/script/modules/django-i18n";
import { getAdDisplayPreferences } from "web/script/utils/ads";
import { ProductLayoutSerializer } from "web/types/serializers";

export function RelatedArea({
    productLayout,
}: {
    productLayout: ProductLayoutSerializer;
}): JSX.Element | null {
    const {
        related_products: relatedProducts,
        curated_ads: curatedAds,
        brand_ad: brandAd,
        adsense_for_shopping: adsenseForShopping,
        related_designers_category_rail: relatedDesignersCategoryRail,
        recently_viewed_products: recentlyViewedProducts,
        related_search_rail: relatedSearchRail,
    } = productLayout;

    const { productPurchaseType } = useInStockProductPageContext();

    const isExpressCheckout = productPurchaseType === ProductPurchaseType.EXPRESS_CHECKOUT;

    const { showCuratedAds, showBrandAds, showAdsense } = getAdDisplayPreferences(
        curatedAds,
        brandAd,
        adsenseForShopping
    );

    const showCuratedAdsTop = !isExpressCheckout && showCuratedAds && curatedAds;
    const showCuratedAdsBottom = isExpressCheckout && showCuratedAds && curatedAds;
    const showBrandAdTop = !isExpressCheckout && showBrandAds && brandAd;
    const showBrandAdBottom = isExpressCheckout && showBrandAds && brandAd;

    const displayShoppingAdsense = showAdsense && adsenseForShopping;
    const isAdsenseAtTop = adsenseForShopping?.is_at_top && !isExpressCheckout;

    return (
        <>
            {showCuratedAdsTop && <CuratedAds {...curatedAds} feedType={""} />}

            {showBrandAdTop && (
                <View>
                    <BrandAdContainer {...brandAd} />
                </View>
            )}

            {displayShoppingAdsense && isAdsenseAtTop && (
                <View padding="sm">
                    <AdsenseShopping {...adsenseForShopping} />
                </View>
            )}

            <VStack spacing="xl">
                {relatedProducts && (
                    <MoreLikeThisGrid section={relatedProducts.sections[0]} isInStock />
                )}

                {showCuratedAdsBottom && (
                    <View>
                        <CuratedAds {...curatedAds} feedType={""} />
                    </View>
                )}

                {showBrandAdBottom && (
                    <View>
                        <BrandAdContainer {...brandAd} />
                    </View>
                )}

                {displayShoppingAdsense && !isAdsenseAtTop && (
                    <AdsenseShopping {...adsenseForShopping} />
                )}

                {relatedDesignersCategoryRail &&
                    !!relatedDesignersCategoryRail.designers?.length && (
                        <RelatedSearchesRail
                            title={relatedDesignersCategoryRail.title || undefined}
                            relatedSearches={relatedDesignersCategoryRail.designers}
                        />
                    )}

                {relatedSearchRail && !!relatedSearchRail.related_searches.length && (
                    <RelatedSearchesRail
                        title={gettext("product.related_products.related_searches")}
                        relatedSearches={relatedSearchRail.related_searches}
                    />
                )}

                {!!recentlyViewedProducts && (
                    <RecentlyViewed
                        title={recentlyViewedProducts.title}
                        products={recentlyViewedProducts.products}
                        is_loading={recentlyViewedProducts.is_loading}
                        current_product_id={recentlyViewedProducts.current_product_id}
                        in_overlay={recentlyViewedProducts.in_overlay}
                        isRedesign
                    />
                )}
            </VStack>
        </>
    );
}
